import React, { useState, useEffect } from "react";
import { Box, Stack, Typography, Paper, IconButton, Collapse, Button, CircularProgress, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { QuizStyledCard } from "../../shared/QuizStyledCard";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { OnetimeProduct, ModalType } from "../types";

interface Props {
  customerId: string;
  addressGroups: Array<{ addressId: string }>;
  onOpenModal: (type: ModalType) => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

export function OnetimesSection({ customerId, addressGroups, onOpenModal, onSuccess, onError }: Props) {
  const [show, setShow] = useState(true);
  const [onetimes, setOnetimes] = useState<OnetimeProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { call } = useRechargeApi();

  const fetchOnetimes = async () => {
    if (!customerId || addressGroups.length === 0) return;
    setLoading(true);
    try {
      const res = await call<any>("/recharge/onetimes/list", { params: { customer_id: customerId } });
      if (res.ok) {
        setOnetimes(res.data?.onetimes || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnetimes();
  }, [customerId, addressGroups.length]);

  const handleDelete = async (onetimeId: string) => {
    setDeletingId(onetimeId);
    try {
      const res = await call("/recharge/onetimes/delete", { method: "POST", body: { onetimeId } });
      if (res.ok) {
        onSuccess("One-time removed");
        setOnetimes(prev => prev.filter(o => o.id !== onetimeId));
      } else {
        onError(res.error || "Failed to delete one-time");
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <QuizStyledCard>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setShow(!show)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShoppingCartIcon sx={{ color: COLORS.brown }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>
            One-times ({onetimes.length})
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(e) => { e.stopPropagation(); onOpenModal("onetime"); }}
            sx={{ fontSize: 11, textTransform: "none" }}
          >
            Add
          </Button>
          <IconButton size="small">
            {show ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={show} timeout="auto" unmountOnExit>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : onetimes.length === 0 ? (
          <Typography sx={{ mt: 2, fontSize: 13, color: COLORS.textMuted }}>No one-time products</Typography>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {onetimes.map(item => (
              <Paper key={item.id} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, color: COLORS.brownDeep }}>{item.product_title}</Typography>
                  <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>
                    Qty: {item.quantity} &bull; £{item.price}
                    {item.variant_title && ` • ${item.variant_title}`}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(item.id)}
                  disabled={deletingId === item.id}
                >
                  {deletingId === item.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              </Paper>
            ))}
          </Stack>
        )}
      </Collapse>
    </QuizStyledCard>
  );
}
